import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import Button from '../Button';
import CustomNotify from '../Notify';
import CSV from '../../utils/CSV/index';
import ExportController from '../../controllers/ExportController';
import runtimeConfig from '../../config/runtimeConfig';
import { useTemplateConfig } from '../../context/TemplateConfigContext';
import CustomTooltip from '../Tooltip.js';

const defProgress = {
    total: 0,
    uploaded: 0
};


const DataComponentTableExport = ({
    data: propData,
    filter: propFilter,
    settingKey: propSettingKey,
    sessionId: propSessionId,
    totalRows: propTotalRows,
    disabled: propDisabled,
    t
}) => {
    const { assets } = useTemplateConfig();
    const [data, setData] = useState(propData);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({...defProgress});

    const itemsRef = React.useRef([]);
    const errorRef = React.useRef(false);
    const csvRef = React.useRef(new CSV());

    useEffect(() => {
        setData(propData);
    }, [propData]);

    const loadIncrement = useCallback((total) => {
        setProgress(prevProgress => {
            const uploaded = prevProgress.uploaded + 1;
            const newProgress = {
                total,
                uploaded
            };

            if (uploaded === total) {
                onEnd();
            }

            return newProgress;
        });
    }, []);

    const onSuccess = useCallback((data) => {
        const cpaUrl = runtimeConfig.cpaUrl;
        if ('success' === data.result) {
            window.location = cpaUrl + data.link;
        } else {
            CustomNotify(data.error);
        }
        setLoading(false);
    }, []);

    const onError = useCallback((error, total) => {
        errorRef.current = true;
        loadIncrement(total);
    }, [loadIncrement]);

    const onEnd = useCallback(() => {
        setLoading(false);
        setProgress({...defProgress});

        if (errorRef.current) {
            const staticNames = t?.dataTable?.export || {};
            CustomNotify(staticNames.error, 'error');
        } else {
            downloadCSV();
        }
    }, [t]);

    const downloadCSV = useCallback(() => {
        itemsRef.current.unshift([getHeader()]);
        let data = itemsRef.current.reduce((accumulator, cur) => {
            return accumulator.concat(cur);
        });

        csvRef.current.download(csvRef.current.convert(data), 'Conversions');
    }, []);

    const getHeader = useCallback(() => {
        const dataArray = Array.isArray(data) ? data : Object.values(data || {});
        return dataArray
            .filter(item => {
                return !item.disabled && item.visible;
            })
            .map(item => {
                return Parser(item.title);
            });
    }, [data]);

    const onDownload = useCallback(() => {
        setLoading(true);

        itemsRef.current = [];
        errorRef.current = false;

        const filter = {
            ...propFilter,
            'sessionId': propSessionId,
            'totalRows': propTotalRows
        };

        ExportController.getAllData(propSettingKey, filter, onSuccess, onError);
    }, [propFilter, propSessionId, propTotalRows, propSettingKey, onSuccess, onError]);

    const staticNames = t?.dataTable?.export || {};

    return (
        <div>
            <CustomTooltip
                id="exportTooltip"
                tooltip={staticNames.title}
            >
                <Button
                    loading={loading}
                    disabled={propDisabled}
                    progress={progress}
                    text={<img src={assets.icons.downArrow} alt=""/>}
                    onClick={onDownload}
                />
            </CustomTooltip>
        </div>
    );
};

DataComponentTableExport.propTypes = {
    filter: PropTypes.object,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    settingKey: PropTypes.string,
    sessionId: PropTypes.string,
    totalRows: PropTypes.number,
    disabled: PropTypes.bool,
    t: PropTypes.object
};

DataComponentTableExport.defaultProps = {
    filter: {}
};

export default withTranslation(DataComponentTableExport);
